import { TFunction } from 'i18next';

import { CameraAngle, CameraMove, StudioFormat, StudioLook } from './studio-contract';

/**
 * Words for the board's vocabulary, as a marketer would say them. Every
 * label has a one-line hint, because "three-quarter" means nothing until you
 * are told it is the classic catalogue angle.
 */

export function cameraLabel(t: TFunction, c: CameraAngle): { label: string; hint: string } {
    switch (c) {
        case 'wide': return { label: t('STUDIO.CAMERA.WIDE', 'Wide'), hint: t('STUDIO.CAMERA.WIDE_HINT', 'The whole room, the piece in its place') };
        case 'eye-level': return { label: t('STUDIO.CAMERA.EYE', 'Eye level'), hint: t('STUDIO.CAMERA.EYE_HINT', 'Straight on, at standing height') };
        case 'three-quarter': return { label: t('STUDIO.CAMERA.THREE_QUARTER', 'Three-quarter'), hint: t('STUDIO.CAMERA.THREE_QUARTER_HINT', 'The classic catalogue angle') };
        case 'low': return { label: t('STUDIO.CAMERA.LOW', 'Low'), hint: t('STUDIO.CAMERA.LOW_HINT', 'Close to the floor, looking across') };
        case 'high': return { label: t('STUDIO.CAMERA.HIGH', 'High'), hint: t('STUDIO.CAMERA.HIGH_HINT', 'Looking down at it and the floor') };
        case 'top-down': return { label: t('STUDIO.CAMERA.TOP', 'Top down'), hint: t('STUDIO.CAMERA.TOP_HINT', 'Straight from above, flat-lay') };
        case 'detail': return { label: t('STUDIO.CAMERA.DETAIL', 'Detail'), hint: t('STUDIO.CAMERA.DETAIL_HINT', 'Close on material and making') };
        case 'macro': return { label: t('STUDIO.CAMERA.MACRO', 'Macro'), hint: t('STUDIO.CAMERA.MACRO_HINT', 'Fibres and grain, very close') };
        case 'over-shoulder': return { label: t('STUDIO.CAMERA.SHOULDER', 'Over the shoulder'), hint: t('STUDIO.CAMERA.SHOULDER_HINT', 'Someone in front, the piece beyond') };
        default: return { label: c, hint: '' };
    }
}

export function moveLabel(t: TFunction, m: CameraMove): { label: string; hint: string } {
    switch (m) {
        case 'static': return { label: t('STUDIO.MOVE.STATIC', 'Still camera'), hint: t('STUDIO.MOVE.STATIC_HINT', 'Only the action moves') };
        case 'push-in': return { label: t('STUDIO.MOVE.PUSH', 'Push in'), hint: t('STUDIO.MOVE.PUSH_HINT', 'Slowly moves closer') };
        case 'pull-out': return { label: t('STUDIO.MOVE.PULL', 'Pull back'), hint: t('STUDIO.MOVE.PULL_HINT', 'Reveals the room') };
        case 'pan': return { label: t('STUDIO.MOVE.PAN', 'Pan'), hint: t('STUDIO.MOVE.PAN_HINT', 'Glides sideways') };
        case 'orbit': return { label: t('STUDIO.MOVE.ORBIT', 'Orbit'), hint: t('STUDIO.MOVE.ORBIT_HINT', 'Arcs around the piece') };
        case 'tilt': return { label: t('STUDIO.MOVE.TILT', 'Tilt up'), hint: t('STUDIO.MOVE.TILT_HINT', 'From the floor up to it') };
        case 'crane-down': return { label: t('STUDIO.MOVE.CRANE', 'Descend'), hint: t('STUDIO.MOVE.CRANE_HINT', 'Comes down from above') };
        case 'tracking': return { label: t('STUDIO.MOVE.TRACK', 'Follow'), hint: t('STUDIO.MOVE.TRACK_HINT', 'Tracks alongside the action') };
        case 'handheld': return { label: t('STUDIO.MOVE.HANDHELD', 'Handheld'), hint: t('STUDIO.MOVE.HANDHELD_HINT', 'Filmed on a phone, natural') };
        default: return { label: m, hint: '' };
    }
}

export function lookLabel(t: TFunction, l: StudioLook): { label: string; hint: string } {
    switch (l) {
        case 'cinematic': return { label: t('STUDIO.LOOK.CINEMATIC', 'Cinematic'), hint: t('STUDIO.LOOK.CINEMATIC_HINT', 'A commercial shot on a cinema camera') };
        case 'editorial': return { label: t('STUDIO.LOOK.EDITORIAL', 'Editorial'), hint: t('STUDIO.LOOK.EDITORIAL_HINT', 'Design-magazine interiors, natural light') };
        case 'ugc': return { label: t('STUDIO.LOOK.UGC', 'Phone video'), hint: t('STUDIO.LOOK.UGC_HINT', 'Real, handheld, made for TikTok and Reels') };
        case 'studio': return { label: t('STUDIO.LOOK.STUDIO', 'Studio'), hint: t('STUDIO.LOOK.STUDIO_HINT', 'Clean packshot on a plain backdrop') };
        case 'warm-film': return { label: t('STUDIO.LOOK.FILM', 'Golden film'), hint: t('STUDIO.LOOK.FILM_HINT', 'Golden hour on 35mm film') };
        default: return { label: l, hint: '' };
    }
}

export function formatLabel(t: TFunction, f: StudioFormat): { label: string; hint: string } {
    switch (f) {
        case '9:16': return { label: '9:16', hint: t('STUDIO.FORMAT.REEL', 'Reels, Stories, TikTok') };
        case '4:5': return { label: '4:5', hint: t('STUDIO.FORMAT.FEED', 'Instagram feed') };
        case '1:1': return { label: '1:1', hint: t('STUDIO.FORMAT.SQUARE', 'Square post') };
        case '16:9': return { label: '16:9', hint: t('STUDIO.FORMAT.WIDE', 'Website, YouTube') };
        default: return { label: f, hint: '' };
    }
}

export function aspectValue(f: StudioFormat): number {
    const [w, h] = f.split(':').map(Number);
    return w / h;
}
